import { useState } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import darkCheckCircleIcon from '../../../static/icons/dark-check-circle.svg';
import CommonPopup from '../../TheoryQuestionFrame/CommonPopup';
import eyeIcon from '../../../static/icons/Eye.svg';
import deleteIcon from '../../../static/img/deletered.svg';
import arrowDownIcon from '../../../static/img/arrowdown2.svg';
import plusBlueIcon from '../../../static/img/Plus-blue.svg';
import QuestionTooltip from '../../../components2/QuestionTooltip';
import questionMark from '../../../static/img/question-mark.svg';

const SortableSuccessCriteriaItem = ({
  id,
  item,
  index,
  onDelete,
  onPreview,
  onSelect,
  allItems,
  selectedIds,
  filterSubject,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id.toString() });

  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <SelectionItem
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
    >
      <SelectionRow>
        <DragHandle {...attributes} {...listeners}>
          <DragDots>
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
            <DragDot />
          </DragDots>
        </DragHandle>
        <SelectWrapper>
          <SelectButton
            onClick={(e) => {
              e.stopPropagation();
              setIsPopupOpen(true);
            }}
          >
                  <span>{item?.title || item?.name || placeholder}</span>
            <SelectArrow src={arrowDownIcon} alt="dropdown" />
          </SelectButton>
        </SelectWrapper>
        <PreviewButton
          onClick={(e) => {
            e.stopPropagation();
            onPreview(item);
          }}
        >
          <PreviewIcon src={eyeIcon} alt="preview" />
        </PreviewButton>
      </SelectionRow>
      <DeleteLink
        onClick={(e) => {
          e.stopPropagation();
          onDelete(index);
        }}
      >
        <DeleteIconSmall src={deleteIcon} alt="delete" />
        Delete
      </DeleteLink>

      {isPopupOpen && (
        <CommonPopup
          isOpen={true}
          onClose={() => setIsPopupOpen(false)}
          allMarkingCriterias={allItems.filter(sc =>
            !selectedIds.includes(sc.id) || sc.id === item?.id
          )}
          updateMarkingCriteria={(selectedItem) => {
            onSelect(selectedItem, index);
            setIsPopupOpen(false);
          }}
          itemType="Success Criterias"
          selectedItem={item}
          hideTypeFilter={true}
          filterSubject={filterSubject}
        />
      )}
    </SelectionItem>
  );
};

export const SuccessCriteriaFeedbackType = ({
  formData,
  updateFormData,
  allSuccessCriteria = [],
  onSuccessCriteriaPreview,
  showLabel = true,
  showTooltip = true,
  filterSubject = null,
  hideGoToLink = false,
  alwaysShowAddAnother = false,
  placeholder = 'Select',
}) => {
  const history = useHistory();
  const [openPopups, setOpenPopups] = useState({});

  const selectedIds = formData.successCriteriaIds || [];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    const oldIndex = selectedIds.indexOf(active.id);
    const newIndex = selectedIds.indexOf(over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newIds = arrayMove(selectedIds, oldIndex, newIndex);
    updateFormData({
      successCriteriaIds: newIds,
    });
  };

  const handleSelect = (successCriteria, index) => {
    const currentIds = [...selectedIds];
    if (index !== undefined) {
      currentIds[index] = successCriteria.id;
    } else {
      if (!currentIds.includes(successCriteria.id)) {
        currentIds.push(successCriteria.id);
      }
    }
    updateFormData({
      successCriteriaIds: currentIds,
    });
    setOpenPopups({});
  };

  const handleDelete = (index) => {
    const currentIds = [...selectedIds];
    currentIds.splice(index, 1);
    updateFormData({
      successCriteriaIds: currentIds,
    });
  };

  const handlePreview = (item) => {
    if (item && onSuccessCriteriaPreview) {
      onSuccessCriteriaPreview(item);
    }
  };

  const getItemById = (id) => {
    return allSuccessCriteria.find(sc => sc.id === id);
  };

  const availableItems = allSuccessCriteria.filter(sc => !selectedIds.includes(sc.id));

  return (
    <Container>
      {showLabel && (
        <InputHeader>
          <OptionIcon>
            <IconImage src={darkCheckCircleIcon} alt="Success Criterias" />
          </OptionIcon>
          <OptionContent>
            <OptionLabel>
              Success Criterias
              {showTooltip && (
                <QuestionTooltip
                  text="Learn more about Success Criterias"
                  img={questionMark}
                />
              )}
            </OptionLabel>
          </OptionContent>
        </InputHeader>
      )}

      <InputContent showLabel={showLabel}>
        {selectedIds.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedIds}
              strategy={verticalListSortingStrategy}
            >
              {selectedIds.map((itemId, index) => {
                const currentItem = getItemById(itemId);
                if (!currentItem) return null;

                return (
                  <SortableSuccessCriteriaItem
                    key={itemId}
                    id={itemId}
                    item={currentItem}
                    index={index}
                    onDelete={handleDelete}
                    onPreview={handlePreview}
                    onSelect={handleSelect}
                    allItems={allSuccessCriteria}
                    selectedIds={selectedIds}
                    filterSubject={filterSubject}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}

        {selectedIds.length === 0 && (
          <SelectionItem>
            <SelectionRow>
              <SelectWrapper>
                <SelectButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpenPopups({ add: true });
                  }}
                >
                  <span>{placeholder}</span>
                  <SelectArrow src={arrowDownIcon} alt="dropdown" />
                </SelectButton>
              </SelectWrapper>
            </SelectionRow>
          </SelectionItem>
        )}

        {(alwaysShowAddAnother || selectedIds.length > 0) &&
          availableItems.length > 0 && (
            <AddButton
              onClick={(e) => {
                e.stopPropagation();
                setOpenPopups({ add: true });
              }}
            >
              <PlusIcon src={plusBlueIcon} alt="add" />
              Add another success criteria
            </AddButton>
          )}

        {openPopups.add && (
          <CommonPopup
            isOpen={true}
            onClose={() => setOpenPopups({})}
            allMarkingCriterias={availableItems}
            updateMarkingCriteria={(selectedItem) => {
              handleSelect(selectedItem);
            }}
            itemType="Success Criteria"
            selectedItem={null}
            hideTypeFilter={true}
            filterSubject={filterSubject}
          />
        )}

        {!hideGoToLink && (
          <GoToLink
            onClick={(e) => {
              e.stopPropagation();
              history.push('/tools/successcriterias');
            }}
          >
            Go to success criterias
          </GoToLink>
        )}
      </InputContent>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const InputHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const OptionIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const IconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const OptionLabel = styled.span`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
  font-weight: 500;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding-left: ${props => props.showLabel ? '56px' : '0'};
`;

const SelectionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  background-color: transparent;
  border-radius: 8px;
  transition: background-color 0.2s;
`;

const SelectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`;

const SelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 10px 12px 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text);
  min-height: 42px;
  overflow: hidden;

  & > span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:focus {
    outline: none;
    border-color: #7200E0;
    box-shadow: 0 0 0 3px rgba(114, 0, 224, 0.1);
  }

  &:hover {
    border-color: #7200E0;
  }
`;

const SelectArrow = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: #7200E0;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    background-color: #5a00b3;
  }
`;

const PreviewIcon = styled.img`
  -webkit-filter: brightness(0) invert(1);
  filter: brightness(0) invert(1);
  width: 22px;
  height: 30px;
`;

const DeleteLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: fit-content;
  margin-left: 36px;

  &:hover {
    color: #dc2626;
  }
`;

const DeleteIconSmall = styled.img`
  width: 16px;
  height: 16px;
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #7200E0;
  border-radius: 20px;
  background-color: white;
  cursor: pointer;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  width: fit-content;

  &:hover {
    background-color: #F1E6FC;
  }
`;

export const PlusIcon = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
`;

const GoToLink = styled.button`
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  width: fit-content;

  &:hover {
    color: #5a00b3;
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-height: 40px;
  cursor: grab;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;

  &:hover {
    background-color: #f3f4f6;
  }

  &:active {
    cursor: grabbing;
  }
`;

const DragDots = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 4px);
  gap: 3px;
`;

const DragDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: #9ca3af;
`;
